"use client";

import type { JSX } from "react";
import { useEffect, useRef, useState } from "react";
import cn from "@/utils/cn";

export interface ProgressBarSingleProps {
  width: number;
  height?: string;
  className?: string;
}

/**
 * @name ProgressBarSingle
 * @description Renders a single progress bar with animation when in view.
 *
 * @param {Object} props - Component props.
 * @param {number} props.width - The width percentage of the progress bar.
 * @param {string} [props.height] - Optional custom height class for the progress bar.
 * @param {string} [props.className] - Optional custom class.
 * @returns {JSX.Element} The rendered single progress bar component.
 *
 * @example
 * <ProgressBarSingle width={75} height="h-4" />
 */
const ProgressBarSingle = ({
  width,
  height = "h-2",
  className
}: ProgressBarSingleProps): JSX.Element => {
  const [isInView, setIsInView] = useState(false);
  const progress = useRef<HTMLProgressElement>(null);

  useEffect(() => {
    const progressRef = progress.current;
    if (!progressRef) return;

    const progressObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          progressObserver.unobserve(entry.target);
        }
      },
      {
        threshold: 0.2
      }
    );

    progressObserver.observe(progressRef);

    return () => {
      progressObserver.disconnect();
    };
  }, []);

  return (
    <progress
      ref={progress}
      value={isInView ? width : 0}
      max={100}
      className={cn(
        "w-full appearance-none overflow-hidden rounded-full border-none align-middle",
        height,
        // Track colors
        "bg-slate-200 dark:bg-slate-700",
        "[&::-webkit-progress-bar]:bg-slate-200 dark:[&::-webkit-progress-bar]:bg-slate-700",
        // Fill colors (current text color)
        "[&::-moz-progress-bar]:bg-current [&::-moz-progress-bar]:transition-all [&::-moz-progress-bar]:duration-1000 [&::-moz-progress-bar]:ease-out",
        "[&::-webkit-progress-value]:bg-current [&::-webkit-progress-value]:transition-all [&::-webkit-progress-value]:duration-1000 [&::-webkit-progress-value]:ease-out",
        className
      )}
    >
      {width}%
    </progress>
  );
};

export default ProgressBarSingle;
